import type { ComponentClass, FunctionComponent } from 'react';
import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';

import {
  createPaymentFromOrder,
  createRecurringReceivable,
  goToStep,
} from 'actions';
import { SurchargeCardFormWrapper } from 'components/CardTokenization/SurchargeCardFormWrapper';
import { Nothing } from 'components/Nothing/Nothing';
import { PaymentConfirmation } from 'components/PaymentConfirmation/PaymentConfirmation';
import { PaymentInformation } from 'components/PaymentInformation/PaymentInformation';
import { PaymentOptions } from 'components/PaymentOptions/PaymentOptions';
import { RecipientInformation } from 'components/RecipientInformation/RecipientInformation';
import { CardPayment as RecurringCardPayment } from 'components/Recurring/CardPayment/CardPayment';
import { DirectDebit } from 'components/Recurring/DirectDebit/DirectDebit';
import { ScrollToTop } from 'components/ScrollToTop/ScrollToTop';
import { SenderInformation } from 'components/SenderInformation/SenderInformation';
import type { PaymentSteps } from 'constants/index';
import {
  RECURRING_RECEIVABLES_ROUTE,
  STEP_CARD_SURCHARGE,
  STEP_CONFIRMATION,
  STEP_OFFERS,
  STEP_PAY,
  STEP_PAYMENT,
  STEP_RECIPIENT,
  STEP_RECURRING_CARD_PAYMENT,
  STEP_RECURRING_DIRECT_DEBIT_PAYMENT,
  STEP_SENDER,
} from 'constants/index';
import { PaymentStepList } from 'containers/PaymentStepList/PaymentStepList';
import type { RootState } from 'reducers/types';
import {
  getCurrentStepIndex,
  getOrder,
  getPaymentStepsForWizard,
  isEmbedded,
  isFetching,
  isPaymentProcess,
  isRecurringOfferSelected,
} from 'selectors';

import { Spinner } from '../Spinner/Spinner';
import { Step as WizardStep } from './components/Step/Step';
import { Steps as WizardSteps } from './components/Steps/Steps';
import { Wizard as Wizr } from './components/Wizard/Wizard';

const StepViews: { [key in PaymentSteps]: React.ComponentType } = {
  [STEP_PAYMENT]: PaymentInformation,
  [STEP_OFFERS]: PaymentOptions,
  [STEP_SENDER]: SenderInformation,
  [STEP_RECIPIENT]: RecipientInformation,
  [STEP_CARD_SURCHARGE]: SurchargeCardFormWrapper,
  [STEP_CONFIRMATION]: PaymentConfirmation,
  [STEP_RECURRING_CARD_PAYMENT]: RecurringCardPayment,
  [STEP_RECURRING_DIRECT_DEBIT_PAYMENT]: DirectDebit,
};

type StepView = keyof typeof StepViews;

type CustomWindow = typeof window & {
  _sift: string[][];
};

type WizardProps = ConnectedProps<typeof connector> & {
  stepIndex: number;
};

const WizardComponent = ({
  createPaymentFromOrder,
  createRecurringReceivable,
  goToStep,
  isEmbedded,
  isPaymentProcessCompleted,
  isRecurring,
  isCreatingRecurringReceivable,
  order: { id, token },
  stepIndex,
  steps,
}: WizardProps) => {
  const handleStepChanged = ({
    activeStepIndex,
  }: {
    activeStepIndex: number;
  }) => {
    (window as CustomWindow)._sift.push(['_trackPageview']);
    return goToStep(activeStepIndex);
  };

  const handleLastWizardStep = async () => {
    if (isRecurring) {
      await createRecurringReceivable();
      return;
    }
    await createPaymentFromOrder();
  };

  if (isCreatingRecurringReceivable) {
    return <Spinner />;
  }

  if (isPaymentProcessCompleted) {
    if (isRecurring) {
      return <Navigate to={RECURRING_RECEIVABLES_ROUTE} />;
    }
    return <Navigate to={`/tracking/${id}?token=${token}`} />;
  }

  return (
    <div className="Wizard" data-testid="wizard">
      <Wizr
        baseUrl={`/${STEP_PAY}`}
        activeStepIndex={stepIndex}
        onStepChanged={handleStepChanged}
        onWizardFinished={handleLastWizardStep}
      >
        {!isEmbedded && <PaymentStepList />}
        <WizardSteps>
          {steps.map(step => {
            const stepId = [
              STEP_CARD_SURCHARGE,
              STEP_RECURRING_CARD_PAYMENT,
              STEP_RECURRING_DIRECT_DEBIT_PAYMENT,
            ].includes(step)
              ? STEP_PAY
              : step;
            const Step =
              (StepViews[step as StepView] as
                | FunctionComponent
                | ComponentClass) ?? Nothing;

            return (
              <WizardStep id={stepId} key={stepId}>
                <div aria-labelledby="Page-title">
                  <ScrollToTop>
                    <Step />
                  </ScrollToTop>
                </div>
              </WizardStep>
            );
          })}
        </WizardSteps>
      </Wizr>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isEmbedded: isEmbedded(state),
  isPaymentProcessCompleted: !isPaymentProcess(state),
  order: getOrder(state),
  stepIndex: getCurrentStepIndex(state),
  steps: getPaymentStepsForWizard(state),
  isRecurring: isRecurringOfferSelected(state),
  isCreatingRecurringReceivable: isFetching(state, 'recurringReceivables'),
});

const mapDispatchToProps = {
  createPaymentFromOrder,
  createRecurringReceivable,
  goToStep,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const Wizard = connector(WizardComponent);

export { Wizard };
